/* eslint-disable complexity */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { config, msg } from 'api';
import getDefaultCataloguePageTitle from '[theme-lib]/apps/catalogue/components/meta/getDefaultCataloguePageTitle';
import { getCatalogueCanonicalLink } from 'apps/catalogue/links';
import { getCategoryName, splitFilters } from './utils';

import { getMetaDescription, prepareFiltersAsParams } from 'apps/meta/utils';

const basicKeywords = [
  'online supermarket',
  'grocery shopping',
  'online groceries',
  'supermarket uk',
  'online delivery',
];

const getLastCategory = (category) => category && category[category.length - 1];

function tryToGetTags(possibleArray) {
  if (!Array.isArray(possibleArray)) {
    return [];
  }

  return possibleArray.map(tag => tag.id);
}

export default class CataloguePageMetadata extends PureComponent {
  static propTypes = {
    basePath: PropTypes.string.isRequired,
    pageMetaData: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
    }),
    pageParameters: PropTypes.shape({
      searchTerm: PropTypes.string,
      category: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.isRequired,
        name: PropTypes.string.isRequired,
      })),
      filters: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.isRequired,
        name: PropTypes.string.isRequired,
      })),
      customFilters: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.isRequired,
        name: PropTypes.string.isRequired,
      })),
    }),
    promotedSearchTitle: PropTypes.string,
    subcategories: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
    })),
    scribeMetaData: PropTypes.shape({
      categoryLandingPageTitle: PropTypes.string,
      categoryPageMetaDescription: PropTypes.string,
      eventLandingPageTitle: PropTypes.string,
      eventPageMetaDescription: PropTypes.string,
    }),
  };

  static defaultProps = {
    subcategories: [],
    pageParameters: {},
  };

  get title() {
    const {
      basePath,
      pageMetaData = {},
      pageParameters,
      promotedSearchTitle,
      scribeMetaData = {},
    } = this.props;

    const { eventLandingPageTitle, categoryLandingPageTitle } = scribeMetaData;

    if (eventLandingPageTitle || categoryLandingPageTitle) {
      return eventLandingPageTitle || categoryLandingPageTitle;
    }

    return getDefaultCataloguePageTitle({
      basePath, pageMetaData, pageParameters, promotedSearchTitle,
    });
  }

  get description() {
    const {
      basePath,
      pageMetaData,
      pageParameters,
      scribeMetaData = {},
    } = this.props;
    const { category, searchTerm } = pageParameters;
    const { categoryPageMetaDescription, eventPageMetaDescription } = scribeMetaData;
    const categoryName = getCategoryName(category);
    let metaDescription = '';

    if (eventPageMetaDescription || categoryPageMetaDescription) {
      return eventPageMetaDescription || categoryPageMetaDescription;
    }

    if (searchTerm) {
      metaDescription = getMetaDescription(pageMetaData, {
        searchTerm, siteTitle: msg.page.title,
      });
      return metaDescription || msg.metaData.getCatalogueDescription(searchTerm);
    }

    if (basePath === '/regulars') {
      return msg.metaData.defaultRegularsDescription;
    }

    let optionalSuffix = '';
    if (basePath === '/on-offer') {
      optionalSuffix = ' offers';
    }

    const categorisedFilters = splitFilters(pageParameters);
    const relevantFiltersAndCategory = [
      ...categorisedFilters.brandFilters,
      categoryName,
      ...categorisedFilters.dietaryFilters,
      ...categorisedFilters.optionFilters,
      ...categorisedFilters.otherFilters,
    ].filter(el => el !== null)
      .join(' ');

    if (relevantFiltersAndCategory) {
      metaDescription = getMetaDescription(pageMetaData, {
        category: categoryName,
        optionalSuffix,
        siteTitle: msg.page.title,
        ...prepareFiltersAsParams(categorisedFilters),
      });
      return metaDescription
        || msg.metaData.getCatalogueDescription(`${relevantFiltersAndCategory}${optionalSuffix}`);
    }

    if (basePath === '/browse') {
      metaDescription = getMetaDescription(pageMetaData, {
        titleSuffix: msg.page.titleSuffix,
        siteTitle: msg.page.title,
      });
      return metaDescription || null;
    }
    if (basePath === '/your-savings-hub') {
      metaDescription = getMetaDescription(pageMetaData, {
        titleSuffix: msg.page.titleSuffix,
        siteTitle: msg.page.title,
      });
      return metaDescription || null;
    }

    return null;
  }

  get alternate() {
    const { pageParameters = {} } = this.props;
    const { searchTerm, category, filters } = pageParameters;

    if (searchTerm) {
      return `/search?entry=${searchTerm}`;
    }

    const tags = [];
    tags.push(...tryToGetTags(category));
    tags.push(...tryToGetTags(filters));

    return `/browse?tags=${tags.join('|')}`;
  }

  get meta() {
    const { alternate } = this;
    const {
      pageParameters = {},
      subcategories,
    } = this.props;
    const { category } = pageParameters;

    const lastCategory = getLastCategory(category);

    const meta = [];

    if (config.iosAppId) {
      meta.push({
        name: 'apple-itunes-app',
        content: `app-id=${config.iosAppId},app-argument=${alternate}`,
      });
    }

    if (category) { // categories will be undefined for main /browse, /on-offer etc, so default keywords will be used
      const subcategoriesNames = subcategories.map(subcategory => subcategory.name).slice(0, 15);
      const buyCategoryOnline = lastCategory && `Buy ${lastCategory.name} online`;
      const keywords = [...basicKeywords, ...subcategoriesNames, buyCategoryOnline];

      meta.push({
        name: 'keywords',
        content: keywords.join(', '),
      });
    }

    const { description } = this;
    if (description) {
      meta.push({
        name: 'description',
        content: description,
      });
    }

    return meta;
  }

  get links() {
    const { alternate } = this;
    const { basePath, pageParameters = {} } = this.props;
    const canonical = getCatalogueCanonicalLink(pageParameters, basePath);

    const links = [
      {
        rel: 'canonical',
        href: `https://${config.domain}${canonical}`,
      },
    ];

    if (config.appLinksEnabled && config.androidAppId) {
      links.push({
        rel: 'alternate',
        href: `android-app://${config.androidAppId}/https/${config.domain}${alternate}`,
      });
    }

    if (config.appLinksEnabled && config.iosAppId) {
      links.push({
        rel: 'alternate',
        href: `ios-app://${config.iosAppId}/https/${config.domain}${alternate}`,
      });
    }

    return links;
  }

  render() {
    return (
      <Helmet
        title={this.title}
        meta={this.meta}
        link={this.links}
      />
    );
  }
}
